import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ShieldCheck, HeadphonesIcon, ArrowUpRight, TrendingUp, BadgeCheck, Wallet } from "lucide-react";
import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import i18n from "@/lib/i18n";
import { useGeolocation } from "@/hooks/useGeolocation";

interface BecomeHostContent {
  badge_text: string;
  title: string;
  title_highlight: string;
  subtitle: string;
  benefits: { title: string; description: string; highlight: string }[];
  cta_text: string;
  cta_link: string;
  stat_value: string;
  stat_label: string;
  images: string[];
}

const DEFAULT_CONTENT: BecomeHostContent = {
  badge_text: "Torne-se Anfitrião",
  title: i18n.t("home.becomeHost.transform") || "Transforme o seu espaço em",
  title_highlight: "rendimento",
  subtitle: i18n.t("home.becomeHost.joinThousands") || "Junte-se a milhares de anfitriões que já monetizam os seus espaços de forma segura e simples.",
  benefits: [
    { title: "Rendimentos", description: i18n.t("home.becomeHost.luandaEarnings") || "Anfitriões em Luanda ganham em média 850.000 Kz/mês com estadias de curta duração.", highlight: "850.000 Kz" },
    { title: "Segurança Garantida", description: "{t('host.kycSystem', 'Sistema de verificação KYC pra proprietários, hóspedes, pagamentos seguros e proteção contra danos.')}", highlight: "" },
    { title: "Gestão Completa", description: "{t('host.support247', 'Suporte 24/7, calendário inteligente, pricing dinâmico e integração com IA.')}", highlight: "" },
  ],
  cta_text: "Começar a Ganhar",
  cta_link: "/anunciar",
  stat_value: "+45%",
  stat_label: "Mais rentável",
  images: [
    "https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?w=400&h=500&fit=crop",
    "https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=400&h=300&fit=crop",
    "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=400&h=300&fit=crop",
    "https://images.unsplash.com/photo-1512917774080-9991f1c4c750?w=400&h=500&fit=crop",
  ],
};

const BENEFIT_STYLES = [
  { icon: Wallet, gradient: "from-emerald-500/20 via-emerald-400/10 to-teal-500/20", iconColor: "text-emerald-600 dark:text-emerald-400", shadow: "shadow-emerald-500/20" },
  { icon: ShieldCheck, gradient: "from-blue-500/20 via-primary/10 to-blue-600/20", iconColor: "text-blue-600 dark:text-blue-400", shadow: "shadow-blue-500/20" },
  { icon: HeadphonesIcon, gradient: "from-primary/20 via-purple-400/10 to-fuchsia-500/20", iconColor: "text-primary dark:text-primary", shadow: "shadow-violet-500/20" },
];

const BecomeHost = () => {
  const { t } = useTranslation();
  const { city } = useGeolocation();
  const [content, setContent] = useState<BecomeHostContent>(DEFAULT_CONTENT);
  const currentCity = city || "Luanda";

  // Fetch real platform stats
  const { data: platformStats } = useQuery({
    queryKey: ["platform-host-stats"],
    queryFn: async () => {
      const [{ count: hostCount }, avgRevenueRes] = await Promise.all([
        supabase.from("properties").select("*", { count: "exact", head: true }).eq("status", "active"),
        (supabase.rpc as any)("get_average_host_revenue").then((r: any) => r).catch(() => ({ data: 850000 })),
      ]);
      const avgRevenue = avgRevenueRes?.data ?? 850000;
      return {
        hostCount: Math.max(hostCount || 0, 500),
        avgRevenue: avgRevenue || 850000,
      };
    },
    staleTime: 300000,
  });

  useEffect(() => {
    supabase
      .from("platform_settings")
      .select("value")
      .eq("key", "become_host_content")
      .maybeSingle()
      .then(({ data }) => {
        if (data?.value) {
          setContent({ ...DEFAULT_CONTENT, ...(data.value as any) });
        }
      });
  }, []);

  const formatRevenue = (value: number) => {
    return new Intl.NumberFormat(i18n.language === 'pt' ? "pt-AO" : "en-US", { style: "currency", currency: "AOA", maximumFractionDigits: 0 }).format(value);
  };

  return (
    <section className="py-16 md:py-24 relative overflow-hidden">
      {/* Elegant background with subtle gradients */}
      <div className="absolute inset-0 bg-gradient-to-br from-background via-muted/20 to-background" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-primary/5 via-transparent to-transparent" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,_var(--tw-gradient-stops))] from-bera-gold/5 via-transparent to-transparent" />

      {/* Decorative elements */}
      <motion.div
        className="absolute top-20 right-10 w-72 h-72 bg-gradient-to-br from-primary/10 to-bera-gold/10 rounded-full blur-3xl"
        animate={{ scale: [1, 1.1, 1], opacity: [0.3, 0.5, 0.3] }}
        transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
      />
      <motion.div
        className="absolute bottom-20 left-10 w-96 h-96 bg-gradient-to-br from-bera-gold/10 to-primary/10 rounded-full blur-3xl"
        animate={{ scale: [1.1, 0.9, 1.1], opacity: [0.2, 0.4, 0.2] }}
        transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
      />

      <div className="container mx-auto px-4 relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, ease: "easeOut" }}
          >
            {/* Badge with sparkle */}
            <motion.div
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-primary/10 to-bera-gold/10 border border-primary/20 mb-6"
              whileHover={{ scale: 1.02 }}
            >
              <span className="text-sm font-semibold text-primary">{t('becomeHost.badge')}</span>
            </motion.div>

            {/* Main heading with better typography */}
            <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold mb-4 tracking-tight leading-tight">
              {t('becomeHost.title')}{" "}
              <span className="text-black">{t('becomeHost.titleHighlight')}</span>
            </h2>

            <p className="text-muted-foreground text-base md:text-lg mb-8 leading-relaxed max-w-lg">
              {t('becomeHost.subtitle')}
            </p>

            {/* Elegant benefit cards */}
            <div className="space-y-4 mb-8">
              {content.benefits.map((benefit, index) => {
                const style = BENEFIT_STYLES[index] || BENEFIT_STYLES[0];
                const IconComp = style.icon;
                const displayHighlight = index === 0 && platformStats ? formatRevenue(platformStats.avgRevenue) : benefit.highlight;
                const displayDescription = index === 0 && platformStats
                  ? benefit.description
                      .replace('850.000 Kz', displayHighlight)
                      .replace('{{amount}}', displayHighlight)
                  : benefit.description
                      .replace('{{amount}}', benefit.highlight || '850.000 Kz');

                const formattedRevenueValue = index === 0 && platformStats
                  ? new Intl.NumberFormat(i18n.language === 'pt' ? "pt-AO" : "en-US", { maximumFractionDigits: 0 }).format(platformStats.avgRevenue)
                  : "850.000";

                return (
                  <motion.div
                    key={benefit.title + index}
                    className="group relative flex gap-5 p-5 rounded-2xl bg-white/50 dark:bg-card/50 backdrop-blur-sm border border-border/30 hover:border-primary/20 hover:shadow-lg hover:shadow-primary/5 transition-all duration-500"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.15, duration: 0.5 }}
                    whileHover={{ y: -2 }}
                  >
                    {/* Icon with gradient background and pulse effect */}
                    <div className={`relative w-14 h-14 rounded-2xl bg-gradient-to-br ${style.gradient} flex-shrink-0 flex items-center justify-center ${style.shadow} shadow-lg group-hover:scale-110 transition-transform duration-500`}>
                      {/* Pulse ring */}
                      <motion.div
                        className={`absolute inset-0 rounded-2xl bg-gradient-to-br ${style.gradient} opacity-60`}
                        animate={{ scale: [1, 1.35, 1.35], opacity: [0.5, 0.15, 0] }}
                        transition={{ duration: 2.5, repeat: Infinity, ease: "easeOut", delay: index * 0.4 }}
                      />
                      <motion.div
                        className={`absolute inset-0 rounded-2xl bg-gradient-to-br ${style.gradient} opacity-40`}
                        animate={{ scale: [1, 1.5, 1.5], opacity: [0.3, 0.1, 0] }}
                        transition={{ duration: 2.5, repeat: Infinity, ease: "easeOut", delay: index * 0.4 + 0.5 }}
                      />
                      <div className="absolute inset-0 rounded-2xl bg-white/40 dark:bg-white/10 backdrop-blur-sm" />
                      <IconComp className={`w-6 h-6 ${style.iconColor} relative z-10`} strokeWidth={1.5} />
                    </div>

                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-display font-bold text-base text-foreground">
                          {t(`becomeHost.benefits.${['earnings', 'security', 'management'][index]}.title`)}
                        </h3>
                        {index === 0 && <BadgeCheck className="w-4 h-4 text-emerald-500" />}
                      </div>
                      <p className="text-muted-foreground text-sm leading-relaxed">
                        {index === 0 ? (
                          <>
                            {i18n.language === 'pt' ? (
                              <>
                                Anfitriões em{" "}
                                <span className="font-bold text-foreground dark:text-white">
                                  {currentCity}
                                </span>{" "}
                                ganham em média mais de{" "}
                                <span className="font-bold text-primary dark:text-[#F37361] bg-primary/5 dark:bg-primary/10 px-1.5 py-0.5 rounded-md">
                                  {formattedRevenueValue.replace(/\./g, " ").replace(/,/g, " ")} Kz/mês
                                </span>{" "}
                                com estadias de curta{" "}
                                <span className="font-semibold text-foreground dark:text-white">
                                  duração
                                </span>{" "}
                                e{" "}
                                <span className="font-bold text-primary dark:text-[#F37361] bg-primary/5 dark:bg-primary/10 px-1.5 py-0.5 rounded-md">
                                  Mensal
                                </span>
                                !
                              </>
                            ) : (
                              <>
                                Hosts in{" "}
                                <span className="font-bold text-foreground dark:text-white">
                                  {currentCity}
                                </span>{" "}
                                earn an average of more than{" "}
                                <span className="font-bold text-primary dark:text-[#F37361] bg-primary/5 dark:bg-primary/10 px-1.5 py-0.5 rounded-md">
                                  {formattedRevenueValue.replace(/\./g, " ").replace(/,/g, " ")} Kz/month
                                </span>{" "}
                                with short-term{" "}
                                <span className="font-semibold text-foreground dark:text-white">
                                  stays
                                </span>{" "}
                                and{" "}
                                <span className="font-bold text-primary dark:text-[#F37361] bg-primary/5 dark:bg-primary/10 px-1.5 py-0.5 rounded-md">
                                  Monthly
                                </span>
                                !
                              </>
                            )}
                          </>
                        ) : (
                          t(`becomeHost.benefits.${['earnings', 'security', 'management'][index]}.description`, displayDescription)
                        )}
                      </p>
                    </div>
                  </motion.div>
                );
              })}
            </div>

            {/* CTA Button with enhanced styling */}
            <motion.div
              className="flex flex-col sm:flex-row items-start sm:items-center gap-4"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.5 }}
            >
              <motion.div whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}>
                <Button
                  size="lg"
                  className="h-14 px-8 rounded-full font-semibold text-base bg-gradient-to-r from-primary to-bera-terracotta shadow-[0_8px_32px_-8px_rgba(180,90,50,0.5)] hover:shadow-[0_12px_40px_-8px_rgba(180,90,50,0.6)] transition-all duration-300"
                  asChild
                >
                  <Link to="/anunciar" className="flex items-center gap-3">
                    {t('becomeHost.cta')}
                    <ArrowUpRight className="w-5 h-5" />
                  </Link>
                </Button>
              </motion.div>

              <p className="text-xs text-muted-foreground">
                {t('becomeHost.hostCount', { count: platformStats?.hostCount.toLocaleString() || "500+" })}
              </p>
            </motion.div>
          </motion.div>

          {/* Image collage with elegant styling */}
          <motion.div
            className="relative lg:pl-8"
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2, ease: "easeOut" }}
          >
            <div className="relative">
              {/* Main image grid with overlapping effect */}
              <div className="grid grid-cols-12 gap-3">
                {/* Large feature image */}
                <motion.div
                  className="col-span-7 row-span-2"
                  whileHover={{ scale: 1.02 }}
                  transition={{ duration: 0.3 }}
                >
                  <div className="relative h-full min-h-[320px] rounded-3xl overflow-hidden shadow-md shadow-primary/10 group">
                    <img
                      src={content.images[0]}
                      alt="Espaço premium"
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent" />
                  </div>
                </motion.div>

                {/* Secondary images */}
                <motion.div
                  className="col-span-5"
                  whileHover={{ scale: 1.02 }}
                  transition={{ duration: 0.3 }}
                >
                  <div className="relative h-40 rounded-2xl overflow-hidden shadow-lg group">
                    <img
                      src={content.images[1]}
                      alt="Interior elegante"
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                    />
                  </div>
                </motion.div>

                <motion.div
                  className="col-span-5"
                  whileHover={{ scale: 1.02 }}
                  transition={{ duration: 0.3 }}
                >
                  <div className="relative h-40 rounded-2xl overflow-hidden shadow-lg group">
                    <img
                      src={content.images[2]}
                      alt="Detalhes de luxo"
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                    />
                  </div>
                </motion.div>
              </div>

              {/* Floating stats card - elegantly positioned */}
              <motion.div
                className="absolute -bottom-6 -left-6 bg-white/95 dark:bg-card/95 backdrop-blur-xl rounded-2xl p-5 shadow-[0_20px_60px_-15px_rgba(0,0,0,0.3)] border border-border/30"
                initial={{ opacity: 0, y: 20, scale: 0.9 }}
                whileInView={{ opacity: 1, y: 0, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 0.6, type: "spring", stiffness: 150 }}
                animate={{ y: [0, -8, 0] }}
              >
                <div className="flex items-center gap-4">
                  <div className="relative w-14 h-14 rounded-2xl bg-gradient-to-br from-primary/15 via-primary/10 to-bera-gold/20 flex items-center justify-center shadow-[inset_0_1px_2px_rgba(255,255,255,0.6),0_4px_12px_-4px_hsl(var(--primary)/0.35)] overflow-hidden">
                    <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(255,255,255,0.5),transparent_55%)]" />
                    <svg viewBox="0 0 32 32" className="relative w-7 h-7" fill="none" aria-hidden="true">
                      <defs>
                        <linearGradient id="growth-line" x1="0" y1="32" x2="32" y2="0" gradientUnits="userSpaceOnUse">
                          <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity="0.55" />
                          <stop offset="100%" stopColor="hsl(var(--primary))" />
                        </linearGradient>
                        <linearGradient id="growth-fill" x1="16" y1="6" x2="16" y2="28" gradientUnits="userSpaceOnUse">
                          <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity="0.22" />
                          <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity="0" />
                        </linearGradient>
                      </defs>
                      <path d="M4 24 L11 17 L16 20 L22 12 L27 8 L27 26 L4 26 Z" fill="url(#growth-fill)" />
                      <path d="M4 24 L11 17 L16 20 L22 12 L27 8" stroke="url(#growth-line)" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
                      <path d="M22 8 L27 8 L27 13" stroke="hsl(var(--primary))" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
                      <circle cx="11" cy="17" r="1.6" fill="white" stroke="hsl(var(--primary))" strokeWidth="1.4" />
                      <circle cx="16" cy="20" r="1.6" fill="white" stroke="hsl(var(--primary))" strokeWidth="1.4" />
                    </svg>
                  </div>
                  <div>
                    <p className="font-display font-bold text-3xl tracking-tight text-foreground leading-none">
                      {content.stat_value}
                    </p>
                    <p className="text-sm text-muted-foreground mt-1 font-medium">{t('host.mostProfitable', content.stat_label)}</p>
                  </div>
                </div>
              </motion.div>

              {/* Trust badge */}
              <motion.div
                className="absolute -top-4 right-4 bg-white/90 dark:bg-card/90 backdrop-blur-sm rounded-full px-4 py-2 shadow-lg border border-border/20 flex items-center gap-2"
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.8 }}
              >
                <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                <span className="text-xs font-semibold text-foreground">{t('common.online')}</span>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default BecomeHost;
